import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
  Box,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import CloseIcon from "@mui/icons-material/Close";
import { roleSidemenuMapService } from "../../utils/services/role-sidemenu-map.service";
import { toast } from "react-toastify";
import { roleService } from "../../utils/services/role.service";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { sidemenuService } from "../../utils/services/sidemenu.service";

const RoleSidemenuModal = ({
  open,
  handleClose,
  onAddData,
  selectedMapping,
}) => {
  const [formData, setFormData] = useState({
    role: null,
    sidemenu: [],
    accessible: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const errors = {};

    if (!formData.role) {
      errors.role = "Role is required";
    }

    if (!formData.sidemenu) {
      errors.sidemenu = "Sidemenu is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (selectedMapping) {
      setFormData({
        role: selectedMapping?.role || null,
        sidemenu: selectedMapping?.sidemenu || null,
        accessible: selectedMapping?.accessible !== false,
      });
    } else {
      initialData();
    }
    return () => {
      initialData();
    };
  }, [selectedMapping]);

  useEffect(() => {
    return () => {
      initialData();
    };
  }, []);

  const initialData = () => {
    setFormData({
      role: null,
      sidemenu: [],
      accessible: true,
    });
    setFormErrors({});
  };

  const handleRoleChange = (role) => {
    setFormData((prev) => ({
      ...prev,
      role: role,
    }));

    // Clear error when user selects
    if (formErrors.role) {
      setFormErrors((prev) => ({
        ...prev,
        role: "",
      }));
    }
  };

  const handleAccessibleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      accessible: e.target.checked,
    }));
  };

  const handleSidemenuChange = (sidemenu) => {
    setFormData((prev) => ({
      ...prev,
      sidemenu: sidemenu,
    }));

    // Clear error when user selects
    if (formErrors.sidemenu) {
      setFormErrors((prev) => ({
        ...prev,
        sidemenu: "",
      }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const dataToSend = (
        Array.isArray(formData.sidemenu)
          ? formData.sidemenu
          : [formData.sidemenu]
      ).map((sidemenu) => ({
        role: formData.role._id,
        sidemenu: sidemenu._id,
        accessible: formData.accessible,
      }));
      console.log(dataToSend);

      const response = await roleSidemenuMapService.create(dataToSend);
      toast.success("Role-sidemenu mapping created successfully");
      onAddData();
      handleClose();
      initialData();
    } catch (error) {
      console.error("Create failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditData = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const dataToSend = {
        role: formData.role._id,
        sidemenu: formData.sidemenu._id,
        accessible: formData.accessible,
      };
      const response = await roleSidemenuMapService.edit(
        selectedMapping._id,
        dataToSend
      );
      toast.success("Role-sidemenu mapping updated successfully");
      onAddData();
      handleClose();
      initialData();
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          padding: 0,
        },
      }}
    >
      <DialogTitle
        style={{
          backgroundColor: "var(--row-bg-even)",
          padding: "16px 24px",
          fontFamily: "Poppins",
          fontSize: 20,
          fontWeight: 600,
          color: "var(--text-tertiary)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {selectedMapping
          ? "Edit Role-Sidemenu Mapping"
          : "Add New Role-Sidemenu Mapping"}
        <IconButton
          onClick={handleClose}
          style={{
            color: "#666666",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider style={{ marginBottom: 2 }} />

      <DialogContent>
        <Grid container spacing={3}>
          {/* Role Selection */}
          <Grid item xs={12} md={12}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "var(--text-heading)",
              }}
            >
              Role <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <AutocompleteWithPagination
              hideLabel
              selectedItem={formData.role}
              customLabel="Role"
              onDataChange={handleRoleChange}
              width="100%"
              optionKey={(role) => role.name}
              apiEndPoint={(limit, skip, search = "") =>
                roleService.fetch(limit, skip, search)
              }
              errorMessage={formErrors.role}
            />
          </Grid>

          {/* Sidemenu Selection */}
          <Grid item xs={12} md={12}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "var(--text-heading)",
              }}
            >
              Sidemenu <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <AutocompleteWithPagination
              multiple
              hideLabel
              selectedItem={formData.sidemenu}
              customLabel="Sidemenu"
              onDataChange={handleSidemenuChange}
              width="100%"
              optionKey={(sidemenu) => sidemenu.title}
              apiEndPoint={(limit, skip, search = "") =>
                sidemenuService.fetch(limit, skip, search)
              }
              errorMessage={formErrors.sidemenu}
            />
          </Grid>

          {/* Accessible Toggle */}
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.accessible}
                  onChange={handleAccessibleChange}
                  color="primary"
                />
              }
              label={
                <CustomTypography
                  style={{
                    fontSize: 14,
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    color: "var(--text-heading)",
                  }}
                >
                  Accessible (unchecked renders as read-only in the sidebar)
                </CustomTypography>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        style={{
          padding: "16px 24px",
          backgroundColor: "var(--row-bg-even)",
          justifyContent: "space-between",
        }}
      >
        <Button
          style={{
            color: "#007DFF",
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
          }}
          variant="outlined"
          onClick={() => {
            handleClose();
            initialData();
          }}
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={(e) => (selectedMapping ? handleEditData(e) : handleSave(e))}
          color="primary"
          variant="contained"
          disabled={loading}
          style={{
            color: "#FFFFFF",
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            width: 100,
            textTransform: "none",
          }}
        >
          {loading ? "Saving..." : selectedMapping ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleSidemenuModal;
